import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Phone, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function MissingPhonesReminder({ contacts, onUpdateContact }) {
  const [open, setOpen] = useState(false);
  const [phones, setPhones] = useState({});
  const [saving, setSaving] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const missingContacts = contacts.filter(c => !c.phone || c.phone.trim() === '');

  useEffect(() => {
    if (dismissed || missingContacts.length === 0) return;
    const timer = setTimeout(() => setOpen(true), 2000);
    return () => clearTimeout(timer);
  }, [missingContacts.length, dismissed]);

  const handleDismiss = () => {
    setDismissed(true);
    setOpen(false);
  };

  const handleSaveAll = useCallback(async () => {
    setSaving(true);
    let count = 0;
    for (const contact of missingContacts) {
      const phone = (phones[contact.id] || '').trim();
      if (phone) {
        try {
          await onUpdateContact({ ...contact, phone });
          count++;
        } catch (e) {}
      }
    }
    setSaving(false);
    if (count > 0) toast.success(`Updated ${count} contact${count > 1 ? 's' : ''}`);
    setOpen(false);
  }, [missingContacts, phones, onUpdateContact]);

  if (missingContacts.length === 0) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleDismiss(); else setOpen(o); }}>
      <DialogContent className="max-w-[95vw] sm:max-w-md max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-[#ff9500]" />
            Missing Phone Numbers
          </DialogTitle>
          <DialogDescription>
            {missingContacts.length} contact{missingContacts.length > 1 ? 's' : ''} without a phone number.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-3 py-2">
          {missingContacts.map((contact) => {
            const postcodes = contact.postcodes || [contact.postcode];
            return (
              <div key={contact.id} className="flex items-center gap-3 p-3 bg-[#f5f5f7] rounded-xl">
                <div className="w-10 h-10 rounded-full bg-[#ff9500]/10 text-[#ff9500] flex items-center justify-center font-semibold text-lg flex-shrink-0">
                  {contact.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-[#1d1d1f]">{contact.name}</div>
                  <div className="flex flex-wrap gap-1 mt-0.5">
                    {postcodes.map((pc, i) => (
                      <span key={i} className="font-mono text-[10px] bg-white px-1.5 py-0.5 rounded">{pc}</span>
                    ))}
                  </div>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <Phone className="w-3.5 h-3.5 text-[#86868b] flex-shrink-0" />
                    <Input
                      type="tel"
                      placeholder="Phone number..."
                      value={phones[contact.id] || ''}
                      onChange={(e) => setPhones(prev => ({ ...prev, [contact.id]: e.target.value }))}
                      className="h-8 text-xs font-mono"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={handleDismiss} className="flex-1">Dismiss</Button>
          <Button onClick={handleSaveAll} disabled={saving} className="flex-1 bg-[#007aff] hover:bg-[#0066d6]">
            {saving ? 'Saving...' : 'Save All'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
